import { useEffect, useState, useCallback } from "react";
import { useSessionCtx } from "../../context/SessionContext";
import {
  SectionHeader,
  Button,
  Toggle,
  EmptyState,
  Icon,
} from "../../components/Elements";
import { MiniSwitch } from "../../components/MiniSwitch.jsx";
import { SettingRow } from "../../components/SettingRow.jsx";
import { FullViewModal, StatusChip, FullViewButton } from "../../components/FullViewModal.jsx";
import { useInteractiveQuizLogic } from "./useInteractiveQuiz.js";
import { MessageText } from "../../constants/constants.js";

function stripHtml(html) {
  if (typeof html !== "string") return "";
  return html.replace(/<[^>]*>/g, "").trim();
}

function getQuizQuestions(poll) {
  if (!poll || typeof poll !== "object") return [];

  const data =
    poll.data && typeof poll.data === "object" && !Array.isArray(poll.data)
      ? poll.data
      : null;

  if (data) {
    const keys = Object.keys(data)
      .filter((k) => /^question\d+$/i.test(k))
      .sort(
        (a, b) =>
          parseInt(a.replace(/\D/g, ""), 10) -
          parseInt(b.replace(/\D/g, ""), 10),
      );
    if (keys.length) {
      const items = keys
        .map((key) => {
          const qData = data[key];
          if (!qData || typeof qData !== "object") return null;
          const qText =
            typeof qData.question === "string" ? stripHtml(qData.question) : "";
          const rawOpts = Array.isArray(qData.options) ? qData.options : [];
          const options = rawOpts
            .map((o, i) => {
              if (!o || typeof o !== "object") return null;
              const label =
                typeof o.label === "string"
                  ? o.label
                  : typeof o.text === "string"
                    ? o.text
                    : String(o.value ?? `Option ${i + 1}`);
              const oid =
                typeof o.option_id === "string"
                  ? o.option_id
                  : typeof o.id === "string"
                    ? o.id
                    : `opt_${o.value != null ? String(o.value) : String(i + 1)}`;
              const isCorrect =
                o.isCorrect === true ||
                o.is_correct === true ||
                o.correct === true ||
                o.isCorrect === 1 ||
                o.is_correct === 1;
              return { option_id: String(oid), text: String(label), isCorrect };
            })
            .filter(Boolean);
          return {
            key,
            question: qText || poll.title || "",
            options,
            timer: poll.timer || poll.duration || 20,
            raw: qData,
          };
        })
        .filter(Boolean);
      if (items.length) return items;
    }
  }

  const fallbackQ =
    typeof poll.question === "string"
      ? stripHtml(poll.question) || poll.title || ""
      : poll.title || "";
  const fallbackOptsRaw = Array.isArray(poll.options) ? poll.options : [];
  const fallbackOpts = fallbackOptsRaw
    .map((o, i) => {
      if (!o || typeof o !== "object") return null;
      const label =
        typeof o.label === "string"
          ? o.label
          : typeof o.text === "string"
            ? o.text
            : String(o.value ?? `Option ${i + 1}`);
      const oid =
        typeof o.option_id === "string"
          ? o.option_id
          : typeof o.id === "string"
            ? o.id
            : `opt_${o.value != null ? String(o.value) : String(i + 1)}`;
      const isCorrect =
        o.isCorrect === true ||
        o.is_correct === true ||
        o.correct === true ||
        o.isCorrect === 1 ||
        o.is_correct === 1;
      return { option_id: String(oid), text: String(label), isCorrect };
    })
    .filter(Boolean);
  if (fallbackQ || fallbackOpts.length) {
    return [
      {
        key: "question1",
        question: fallbackQ || "No question set",
        options: fallbackOpts,
        timer: poll.timer || poll.duration || 20,
        raw: poll,
      },
    ];
  }
  return [];
}

// embedded: rendered inside the MainPage accordion (hides this page's own SectionHeader).
// inline: rendered as in-screen block content (no modal/backdrop) that fills its column -
// MainPage swaps the control tabs for this view while a presentation is running.
// filter: "all" | "active" | "inactive" - applied by the ControlTabs radio filter.
export default function InteractiveQuiz({ embedded = false, inline = false, filter = "all" } = {}) {
  const ctx = useSessionCtx();
  const quizLogic = useInteractiveQuizLogic(ctx);
  // Hooks below must run on every render, so read ctx defensively and return null after them.
  const {
    interactiveQuizzes = [],
    presMode,
    presSlide,
    presTimeLeft,
    presTargetId,
    startPresentation,
    closePresentation,
    nextSlide,
    prevSlide,
    activeInteractiveQuizzes = [],
    interactiveQuizToggle,
  } = ctx || {};

  const {
    hasResult,
    resultData,
    loading,
    resultShown,
    showingResultId,
    getResult,
    setShowResult,
    toggleResultForQuiz,
    resetResultShown,
    finish,
  } = quizLogic;

  // The quiz started from its card; falls back to the first active quiz as before.
  const presentationPoll =
    (presTargetId != null && interactiveQuizzes.find((k) => String(k.id) === String(presTargetId))) ||
    activeInteractiveQuizzes[0] || interactiveQuizzes[0] || null;
  const totalQuestions = (() => {
    if (!presentationPoll) return activeInteractiveQuizzes.length;
    const qs = getQuizQuestions(presentationPoll);
    if (qs.length) return qs.length;
    const qc = Number(presentationPoll.question_count);
    return Number.isFinite(qc) && qc > 0
      ? qc
      : activeInteractiveQuizzes.length || 1;
  })();

  // Only the inline instance (the right column during a presentation) renders the
  // presentation itself; a non-inline instance - e.g. the Quiz tab inside MainPage's
  // Others modal - keeps showing the quiz list instead of a duplicate presentation.
  // It also keeps the result-fetching effects off that second instance.
  const showPresentation = Boolean(presMode && inline);

  const pastLastQuestion = showPresentation && presSlide > totalQuestions;
  // Result slide: fetch the result for the admin only; publishing waits for Show Result.
  useEffect(() => {
    if (pastLastQuestion && !hasResult && !loading) getResult();
  }, [pastLastQuestion, hasResult, loading, getResult]);
  // Going back to a question already published showresult: false; keep the toggle in sync.
  useEffect(() => {
    if (!pastLastQuestion && resultShown) resetResultShown();
  }, [pastLastQuestion, resultShown, resetResultShown]);
  if (!ctx) return null;
  const getQuestionForSlide = (slide) => {
    if (!presentationPoll) return null;
    const qs = getQuizQuestions(presentationPoll);
    if (qs.length) {
      const q = qs[slide - 1];
      if (q)
        return {
          question: q.question,
          options: q.options,
          timer: q.timer || presentationPoll.timer || 20,
          raw: q.raw,
        };
    }
    return activeInteractiveQuizzes[slide - 1] || null;
  };

  const visibleQuizzes =
    filter === "all"
      ? interactiveQuizzes
      : interactiveQuizzes.filter((k) => (filter === "active" ? k.isApproved === true : k.isApproved !== true));

  return (
    <>
      {!showPresentation && (
      <div className="w-full max-w-4xl">
        {!embedded && (
        <SectionHeader
          title="Interactive Quiz"
          icon="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          description="Create engaging quizzes for your audience"
          badge={`${interactiveQuizzes.filter((k) => k.isApproved === true).length}/${interactiveQuizzes.length} active`}
        />
        )}
        {interactiveQuizzes.length === 0 && (
          <EmptyState
            icon="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            title={MessageText.emptyQuizzes.title}
            description={MessageText.emptyQuizzes.description}
          />
        )}
        {interactiveQuizzes.length > 0 && visibleQuizzes.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-6">No {filter} quizzes</p>
        )}
        <div className="space-y-4">
          {visibleQuizzes.map((k) => (
            <QuizCard
              key={String(k.id)}
              quiz={k}
              showingResultId={showingResultId}
              onToggleActive={interactiveQuizToggle}
              onToggleResult={toggleResultForQuiz}
              onStart={startPresentation}
            />
          ))}
        </div>
        {/* <div className="mt-8 pt-6 border-t border-gray-200">
          <Button
            variant="danger"
            size="md"
            aria-label="Clear all quizzes"
            onClick={() => {
              if (
                window.confirm(
                  "Clear all quizzes? This will instantly remove all active quizzes from viewers and clear history for new joiners.",
                )
              ) {
                turnOffAllQuizzes?.();
              }
            }}
          >
            <Icon name="trash" className="w-4 h-4 mr-2" /> Clear All Quizzes
          </Button>
          <p className="text-xs text-gray-500 mt-2">
            Removes active quizzes for live viewers and clears history for new
            joiners.
          </p>
        </div> */}
      </div>
      )}

      {showPresentation && (
        // Fixed height (not content-driven): with the 55/45 column split, aspect 16/11 makes
        // the card exactly as tall as the 16:9 player in the center column, so intro, question
        // and result slides never change its height - only the body below scrolls.
        <div
          className={`w-full flex flex-col overflow-hidden bg-white rounded-2xl border border-gray-200/70 shadow-sm ${
            inline
              ? "aspect-[16/11] min-h-[420px] lg:min-h-[560px] lg:max-h-[720px]"
              : "max-w-4xl min-h-[420px]"
          }`}
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-r from-gray-800 to-black">
            <div className="flex items-center gap-2 min-w-0">
              <svg
                className="w-5 h-5 text-white shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <h2 className="text-sm sm:text-base font-bold text-white truncate">
                Quiz Presentation
              </h2>
            </div>
            <button
              aria-label="Exit presentation"
              onClick={() => {
                try {
                  closePresentation();
                } catch { /* presentation may already be closed */ }
                try {
                  finish();
                } catch { /* finish is idempotent — a double-call is harmless */ }
              }}
              className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors shrink-0"
            >
              <Icon name="close" />
            </button>
          </div>

            <div className="flex-1 min-h-0 p-4 overflow-y-auto overscroll-contain">
            {presSlide === 0 ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-gray-800 to-black flex items-center justify-center">
                  <svg
                    className="w-7 h-7 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-3">
                  Quiz Time!
                </h2>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-800 to-black text-white rounded-2xl text-base font-bold mb-4">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {totalQuestions} Questions
                </div>
                <p className="text-sm text-gray-500 mb-5">
                  Get ready! Click "Start Quiz" to begin.
                </p>
                <div className="mx-auto text-left p-3.5 bg-emerald-50 rounded-2xl border-l-4 border-emerald-500">
                  <h4 className="font-semibold text-emerald-800 mb-2 text-sm">
                    Instructions:
                  </h4>
                  <ul className="text-xs text-emerald-700 space-y-1 ml-5 list-disc">
                    <li>Each question has a time limit</li>
                    <li>Select the correct answer from options</li>
                    <li>Points are awarded for correct answers</li>
                  </ul>
                </div>
              </div>
            ) : presSlide <= totalQuestions ? (
              (() => {
                const quiz = getQuestionForSlide(presSlide);
                if (!quiz) return null;
                const pct = (presTimeLeft / (quiz.timer || 20)) * 100;
                const isLow = presTimeLeft <= 5;
                const isMedium = presTimeLeft <= 10;
                return (
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2.5">
                      <Icon name="clock" />
                      <span
                        className={`text-lg font-bold px-3 py-1.5 rounded-xl ${isLow ? "bg-red-100 text-red-600" : isMedium ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
                      >
                        {presTimeLeft}s
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${isLow ? "bg-gradient-to-r from-red-500 to-red-600" : isMedium ? "bg-gradient-to-r from-amber-500 to-amber-600" : "bg-gradient-to-r from-emerald-500 to-emerald-600"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-5 leading-snug break-words">
                      {quiz.question}
                    </h3>
                    {/* Uniform neutral options (A/B/C/D) - colour is reserved for status
                        (timer) and the correct-answer reveal below, not per option. */}
                    <div className="grid grid-cols-1 gap-2.5 mb-5">
                      {(quiz.options || []).map((opt, i) => (
                        <div
                          key={opt.option_id}
                          className="flex items-center gap-2.5 w-full text-left px-3 py-2.5 rounded-xl border border-gray-200 bg-white"
                        >
                          <span className="w-6 h-6 shrink-0 rounded-md bg-gray-900 text-white text-[11px] font-bold flex items-center justify-center">
                            {String.fromCharCode(65 + (i % 26))}
                          </span>
                          <span className="flex-1 min-w-0 text-sm font-semibold text-gray-800 break-words">
                            {opt.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()
            ) : !hasResult ? (
              <div className="flex flex-col items-center justify-center py-12">
                <h3 className="text-lg font-bold text-gray-800 mb-2 animate-pulse">
                  Fetching results…
                </h3>
                <p className="text-xs text-gray-500 text-center">
                  Viewers are already seeing the leaderboard skeleton
                </p>
              </div>
            ) : (
              <>
                <p
                  className={`text-center text-xs font-medium mb-2 ${resultShown ? "text-emerald-700" : "text-gray-500"}`}
                >
                  {resultShown
                    ? "Result is visible to viewers"
                    : "Result is hidden from viewers. Toggle 'Show Result' to publish."}
                </p>
                <Leaderboard data={resultData} />
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 bg-gray-50 border-t border-gray-200">
            <Button
              onClick={prevSlide}
              disabled={presSlide === 0}
              variant={presSlide === 0 ? "ghost" : "secondary"}
              size="sm"
            >
              <Icon name="chevron-left" />
              <span className="ml-1">Previous</span>
            </Button>
            <div className="text-xs font-medium text-gray-500">
              {presSlide === 0
                ? "Intro"
                : presSlide > totalQuestions
                  ? "Complete"
                  : `Quiz ${presSlide} of ${totalQuestions}`}
            </div>
            <div className="flex items-center gap-2">
              {pastLastQuestion && (
                <Toggle
                  checked={resultShown}
                  onChange={(v) => setShowResult(v)}
                  label="Show Result"
                  disabled={loading || !hasResult}
                />
              )}
              <Button
                onClick={presSlide > totalQuestions ? finish : nextSlide}
                variant="primary"
                size="sm"
              >
                <span className="mr-1">
                  {presSlide === 0
                    ? "Start Quiz"
                    : presSlide > totalQuestions
                      ? "Finish"
                      : "Next"}
                </span>
                <Icon name="chevron-right" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Full quiz in a modal: every question with its options (correct answer highlighted).
function QuizFullView({ quiz: k, questions, title, onClose }) {
  const fallbackOptions = Array.isArray(k.options) ? k.options : [];
  return (
    <FullViewModal
      labelId={`quiz-full-${k.id}`}
      onClose={onClose}
      meta={
        <>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Quiz</span>
          <StatusChip active={k.isApproved === true} />
          <span className="text-[11px] text-gray-500">{k.timer || k.duration || 20}s per question</span>
        </>
      }
      title={<span className="text-base font-semibold text-gray-900">{title}</span>}
    >
      {questions.length ? (
        questions.map((q, i) => (
          <div key={q.key}>
            <div className="flex gap-2">
              <span className="text-xs font-semibold text-gray-400 shrink-0 mt-0.5">Q{i + 1}</span>
              <p className="flex-1 min-w-0 text-sm font-semibold text-gray-900 break-words">{q.question || "No question set"}</p>
            </div>
            {q.options.length ? <QuizOptionList options={q.options} /> : <p className="mt-2 text-xs text-gray-400 italic">No options configured</p>}
          </div>
        ))
      ) : fallbackOptions.length ? (
        <QuizOptionList options={fallbackOptions} />
      ) : (
        <p className="text-sm text-gray-400">No questions configured for this quiz.</p>
      )}
    </FullViewModal>
  );
}

// Lettered option rows; the correct answer is emerald with a "Correct" tag.
function QuizOptionList({ options }) {
  return (
    <div className="space-y-1.5 mt-2">
      {options.map((o, i) => (
        <div
          key={o.option_id ?? i}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${o.isCorrect ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-gray-50 border-gray-200 text-gray-800"}`}
        >
          <span className="w-5 h-5 rounded-md bg-white border border-gray-200 text-[10px] font-semibold text-gray-500 flex items-center justify-center shrink-0">
            {String.fromCharCode(65 + (i % 26))}
          </span>
          <span className="flex-1 min-w-0 break-words">{o.text}</span>
          {o.isCorrect && <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 shrink-0">Correct</span>}
        </div>
      ))}
    </div>
  );
}

// Quiz card in the admin theme (same pattern as the Poll card):
//   header   - icon, title (max 2 lines), type + status + meta; Full view button
//   settings - Active, Show Result (leaderboard-only mode) and, while active, Presentation (Start)
function QuizCard({ quiz: k, showingResultId, onToggleActive, onToggleResult, onStart }) {
  const [fullOpen, setFullOpen] = useState(false);
  const closeFull = useCallback(() => setFullOpen(false), []);
  const questions = getQuizQuestions(k);
  const title = k.title || questions[0]?.question || stripHtml(k.question) || "Interactive Quiz";
  const active = k.isApproved === true;
  const resultsShown = active && k.showresult === true;
  const showingThis = showingResultId === String(k.id);

  return (
    <article
      className={`w-full max-w-xl bg-white rounded-2xl border overflow-hidden transition-shadow hover:shadow-sm ${active ? "border-emerald-200" : "border-gray-200/70"}`}
    >
      {/* Header */}
      <header className="flex items-start gap-3 p-3.5">
        <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-gray-800 to-black text-white flex items-center justify-center shrink-0">
          <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M2 6h20v12H2z M12 12h.01 M17 12h.01 M7 12h.01" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 break-words">{title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Quiz</span>
            {k.id != null && (
              <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 tabular-nums" title="Quiz ID">
                ID {String(k.id)}
              </span>
            )}
            <StatusChip active={active} />
            {resultsShown && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-900 text-white">Results visible</span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-gray-500">
            {questions.length} {questions.length === 1 ? "question" : "questions"} · {k.timer || k.duration || 20}s each
            {Number(k.points) > 0 ? ` · ${k.points} pts` : ""}
          </p>
        </div>
        <FullViewButton onClick={() => setFullOpen(true)} />
      </header>

      {/* Settings */}
      <div className="border-t border-gray-100 bg-gray-50/60 divide-y divide-gray-100">
        <SettingRow label="Active" help={active ? "Visible to viewers" : "Hidden from viewers"}>
          <MiniSwitch
            checked={active}
            onChange={(v) => onToggleActive?.(String(k.id), v)}
            label=""
            ariaLabel="Active"
            hint="Show this quiz to viewers"
          />
        </SettingRow>
        <SettingRow label="Show Result" help={showingThis ? "Publishing the leaderboard…" : "Viewers see only the leaderboard"}>
          <MiniSwitch
            checked={resultsShown}
            onChange={(v) => {
              if (showingResultId == null) onToggleResult(k, v);
            }}
            label=""
            ariaLabel="Show Result"
            hint="Show only the leaderboard to viewers"
          />
        </SettingRow>
        {/* Presentation is offered only once the quiz is active for viewers. */}
        {active && (
        <div className="flex justify-center px-3.5 py-3">
          <button
            type="button"
            onClick={() => onStart(k)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold shadow-sm hover:bg-emerald-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
            Start Presentation
          </button>
        </div>
        )}
      </div>

      {fullOpen && <QuizFullView quiz={k} questions={questions} title={title} onClose={closeFull} />}
    </article>
  );
}

function Leaderboard({ data }) {
  const list =
    data && data.length
      ? data.map((u) => ({
          ...u,
          avatar: u.name ? u.name.charAt(0).toUpperCase() : "U",
        }))
      : [];
  const top3 = list.slice(0, 3);
  const rest = list.slice(3);
  const colors = [
    { bg: "from-yellow-400 to-yellow-600", badge: "🥇" },
    { bg: "from-gray-300 to-gray-500", badge: "🥈" },
    { bg: "from-orange-400 to-orange-600", badge: "🥉" },
  ];

  return (
    <div className="w-full">
      <div className="bg-white rounded-2xl border border-gray-200 w-full overflow-hidden">
        <div className="bg-gradient-to-r from-orange-600 to-red-600 px-4 py-4 text-center">
          <div className="text-3xl mb-1.5">🏆</div>
          <h2 className="text-white text-xl font-bold">Quiz Complete!</h2>
          <p className="text-orange-100 mt-1 text-xs">
            Here are the top performers
          </p>
        </div>
        <div className="p-4">
          <div className="flex items-end justify-center gap-2 mb-5 min-h-[150px]">
            {top3.map((user, i) => {
              const c = colors[i];
              const heights = ["h-[100px] sm:h-[120px]", "h-[80px] sm:h-[95px]", "h-[62px] sm:h-[75px]"];
              const order = i === 1 ? "-order-1" : "";
              return (
                <div
                  key={user.id}
                  className={`flex flex-col items-center ${order} flex-1 min-w-0`}
                >
                  <div
                    className={`w-9 h-9 rounded-full bg-gradient-to-br ${c.bg} flex items-center justify-center text-base mb-2 shadow-lg`}
                  >
                    {user.avatar}
                  </div>
                  <div
                    className={`bg-gradient-to-b ${c.bg} rounded-t-xl ${heights[i]} w-16 sm:w-20 flex flex-col items-center justify-end p-1.5 shadow-lg`}
                  >
                    <div className="text-base sm:text-lg mb-0.5">
                      {c.badge}
                    </div>
                    <div className="text-white font-bold text-[11px] truncate w-full text-center">
                      {user.name}
                    </div>
                    <div className="text-white font-black text-xs">
                      {user.points} pts
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {rest.length > 0 && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 h-px bg-gray-200" />
                <div className="text-gray-400 text-xs font-medium">
                  Other Participants
                </div>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="space-y-2 max-h-[160px] overflow-y-auto">
                {rest.map((user, i) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 bg-gray-50"
                  >
                    <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center font-bold text-[11px] text-gray-600 shrink-0">
                      {i + 4}
                    </div>
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-xs text-white shrink-0">
                      {user.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-800 text-xs truncate">
                        {user.name}
                      </div>
                    </div>
                    <div className="text-xs font-bold text-red-600 shrink-0">
                      {user.points} pts
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
